/**
 * Shared utilities for ProseMirror plugin state management.
 *
 * ## Gate Pattern
 *
 * Plugins use gates to control when `appendTransaction` runs:
 * 1. `apply()` returns NEW object (trigger) or SAME object (skip)
 * 2. `appendTransaction` checks `oldState[key] === newState[key]`
 *
 * ## Available Gates
 *
 * - `makeCountDeltaGate`: triggers when node count changes (for reindexing)
 * - `makePresenceGate`: triggers when node is touched (for tracking)
 *
 * For nodes with volatile attrs (not stored in HTML), implement a custom
 * gate in the node file itself. See HeadingNode.tsx for an example.
 *
 * ## System Transaction Marking
 *
 * Use `markSystemTr` to tag transactions generated by plugins (not by user input).
 * The template locking plugin and the nested-insertion guard use `isSystemTr` to skip these, the AI edit apply among them.
 */

import { Plugin, PluginKey, type EditorState, type Transaction } from '@tiptap/pm/state';
import type { Node as PMNode } from '@tiptap/pm/model';

import type { TMap } from '@shared/types';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

/** Gate state with timestamp. Use -1 for uninitialized. */
export type TGateState = { updatedTime: number };

type TDiffBounds = { start: number; oldEnd: number; newEnd: number } | null;

// ─────────────────────────────────────────────────────────────────────────────
// Diff Utilities
// ─────────────────────────────────────────────────────────────────────────────

/** Compute the changed region between two states. Returns null if identical. */
export const diffBounds = (oldState: Pick<EditorState, 'doc'>, newState: Pick<EditorState, 'doc'>): TDiffBounds => {
  const start = oldState.doc.content.findDiffStart(newState.doc.content);
  if (start == null) return null;

  const diffEnd = oldState.doc.content.findDiffEnd(newState.doc.content);
  const oldEnd = diffEnd ? diffEnd.a : oldState.doc.content.size;
  const newEnd = diffEnd ? diffEnd.b : newState.doc.content.size;

  const clamp = (p: number, max: number) => Math.max(0, Math.min(p, max));
  return {
    start,
    oldEnd: clamp(oldEnd, oldState.doc.content.size),
    newEnd: clamp(newEnd, newState.doc.content.size),
  };
};

/** Check for a node type or a mark carried by any node in the range. */
export const hasNodeOrMarkInRange = (
  doc: PMNode,
  from: number,
  to: number,
  nodeName?: string,
  markName?: string
): boolean => {
  let found = false;
  doc.nodesBetween(from, to, (n) => {
    if (n.type.name === nodeName || (markName && n.marks.some((mark) => mark.type.name === markName))) {
      found = true;
      return false;
    }
    return !found;
  });
  return found;
};

/** Count nodes of type in range. */
export const countNodesInRange = (doc: PMNode, from: number, to: number, nodeName: string): number => {
  let cnt = 0;
  doc.nodesBetween(from, to, (n) => {
    if (n.type.name === nodeName) {
      cnt++;
      return false;
    }
    return true;
  });
  return cnt;
};

// ─────────────────────────────────────────────────────────────────────────────
// Gate Factories
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Gate: triggers when node is touched in the diff region.
 * Use for: tracking/observing changes (e.g., building a map of captions).
 */
export const makePresenceGate = (nodeName: string) => {
  return function applyPresenceGate(
    tr: Transaction,
    value: TGateState,
    oldState: EditorState,
    newState: EditorState
  ): TGateState {
    if (!tr.docChanged && value.updatedTime >= 0) return value;

    const diff = diffBounds(oldState, newState);
    if (!diff) return value;

    const touched =
      hasNodeOrMarkInRange(oldState.doc, diff.start, diff.oldEnd, nodeName) ||
      hasNodeOrMarkInRange(newState.doc, diff.start, diff.newEnd, nodeName);

    if (!touched && value.updatedTime >= 0) return value;
    return { updatedTime: Date.now() };
  };
};

/**
 * Gate: triggers when node count changes in the diff region.
 * Use for: reindexing nodes with PERSISTED attributes (stored in HTML).
 */
export const makeCountDeltaGate = (nodeName: string) => {
  return function applyCountDeltaGate(
    tr: Transaction,
    value: TGateState,
    oldState: EditorState,
    newState: EditorState
  ): TGateState {
    if (!tr.docChanged && value.updatedTime >= 0) return value;

    const diff = diffBounds(oldState, newState);
    if (!diff) return value;

    const oldCount = countNodesInRange(oldState.doc, diff.start, diff.oldEnd, nodeName);
    const newCount = countNodesInRange(newState.doc, diff.start, diff.newEnd, nodeName);

    if (oldCount === newCount && value.updatedTime >= 0) return value;
    return { updatedTime: Date.now() };
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// System Transaction Marking
// ─────────────────────────────────────────────────────────────────────────────

const SYSTEM_META = 'systemTransaction';
const SYSTEM_KEEPS_AUTOSAVE_META = 'systemTransactionKeepsAutoSave';

/**
 * Mark a transaction as system-level (generated by a plugin, not by user input).
 * System transactions bypass the template locking plugin and the nested-insertion guard (the AI edit apply too)
 * and are excluded from the undo history.
 *
 * **Use for:**
 * - `appendTransaction()` return values
 * - Plugin `view()` init / observer dispatches
 * - Programmatic commands that update derived state (e.g., `bumpCaptionLabelNonce`)
 *
 * **Do NOT use for:**
 * - `handleKeyDown` / `handleDOMEvents` handlers (user-initiated)
 * - Editor commands triggered by toolbar buttons or keyboard shortcuts
 * - Paste / drop handlers
 */
export const markSystemTr = (
  tr: Transaction,
  options?: { keepInHistory?: boolean; keepAutoSave?: boolean }
): Transaction => {
  tr.setMeta(SYSTEM_META, true);
  // Most system transactions only touch derived state; one that lands in the saved html says so.
  if (options?.keepAutoSave) tr.setMeta(SYSTEM_KEEPS_AUTOSAVE_META, true);
  // A repair that only makes sense alongside the edit that provoked it stays in history, so one undo
  // reverts both. Taking it out instead leaves the undo restoring a document that never existed:
  // `getRequiredTitlePlugin` put a title back after a select-all-delete, and with the insert outside
  // history the undo of that delete stopped restoring the content at all.
  if (!options?.keepInHistory) tr.setMeta('addToHistory', false);
  return tr;
};

/** Check if a transaction was marked as system-level via `markSystemTr`. */
export const isSystemTr = (tr: Transaction): boolean => {
  return !!tr.getMeta(SYSTEM_META);
};

/** A system transaction whose change belongs in the saved html. */
export const systemTrKeepsAutoSave = (tr: Transaction): boolean => !!tr.getMeta(SYSTEM_KEEPS_AUTOSAVE_META);

// ─────────────────────────────────────────────────────────────────────────────
// Auto-Index Plugin Factory
// ─────────────────────────────────────────────────────────────────────────────

/** A pending index update produced by an auto-index `collectPatches` function. */
export type TAutoIndexPatch = { pos: number; attrs: TMap<string, unknown>; want: number };

export const applyAutoIndexPatches = (tr: Transaction, patches: readonly TAutoIndexPatch[]): Transaction => {
  patches.forEach((patch) => tr.setNodeMarkup(patch.pos, undefined, { ...patch.attrs, index: patch.want }));
  return tr;
};

type TGateFn = (tr: Transaction, value: TGateState, oldState: EditorState, newState: EditorState) => TGateState;

export type TAutoIndexPluginOptions = {
  /** Unique plugin key name (also used to derive an internal re-entry meta). */
  pluginName: string;
  /** Node type name. Used by the count-delta gate that triggers reindexing. */
  nodeName: string;
  /** Meta key callers dispatch to force a reindex after content swaps. */
  resetMeta: string;
  /**
   * Walks the whole doc and returns the set of nodes whose `index` attr
   * needs to change. Pure (no side effects) so it can be unit-tested
   * against a handcrafted state.
   */
  collectPatches: (state: EditorState) => TAutoIndexPatch[];
  /**
   * Gate factory. Defaults to {@link makeCountDeltaGate} - triggers when the
   * count of target nodes changes in the diff region. Pass {@link makePresenceGate}
   * for nodes that also need reindexing on attribute-only changes (e.g. a
   * literature-citation whose `literatureId` is swapped via setNodeMarkup).
   */
  makeGate?: (nodeName: string) => TGateFn;
};

/**
 * Factory for plugins that assign numeric `index` attributes to nodes based
 * on some derivation of doc state (e.g. caption ordering, literature-citation
 * dedup). Consolidates the boilerplate shared by captions and literature
 * citations: count-delta gate, reset-meta handling, re-entry guard,
 * reverse-ordered `setNodeMarkup` patch loop, and `markSystemTr`.
 *
 * Callers provide a `collectPatches` that encodes the numbering rule and
 * a `resetMeta` constant that `setContentAndNotify` dispatches to force a
 * reindex on the next transaction.
 */
export const makeAutoIndexPlugin = ({
  pluginName,
  nodeName,
  resetMeta,
  collectPatches,
  makeGate = makeCountDeltaGate,
}: TAutoIndexPluginOptions) => {
  const key = new PluginKey<TGateState>(pluginName);
  const internalMeta = `${pluginName}:internal`;
  const gate = makeGate(nodeName);

  return new Plugin<TGateState>({
    key,

    state: {
      init: () => ({ updatedTime: -1 }),
      apply(tr, value, oldState, newState) {
        if (tr.getMeta(internalMeta)) return value;
        if (tr.getMeta(resetMeta)) return { updatedTime: -1 };
        return gate(tr, value, oldState, newState);
      },
    },

    appendTransaction(trs, oldState, newState) {
      // @ts-expect-error – access plugin state snapshots
      if (oldState[key.key] === newState[key.key]) return null;
      if (trs.some((tr) => tr.getMeta(internalMeta))) return null;

      const patches = collectPatches(newState);
      if (!patches.length) return null;

      const tr = applyAutoIndexPatches(newState.tr, patches);
      tr.setMeta(internalMeta, true);
      return markSystemTr(tr);
    },
  });
};
